﻿import {
  AlertCircle,
  Loader2,
  Plus,
  Tag,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useT } from "@/lib/i18n";
import type { Promotion } from "@ticketnjoy/types";

import { DashboardPage } from "@/components/dashboard-page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { usePromotionsQuery } from "@/features/promotions/api";

export function PromotionsPage() {
  return (
    <DashboardPage>
      <PromotionsView />
    </DashboardPage>
  );
}

function PromotionsView() {
  const t = useT();
  const { data, isLoading, error } = usePromotionsQuery();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("promotions.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("promotions.subtitle")}
          </p>
        </div>
        <Button asChild>
          <Link to="/promotions/new">
            <Plus className="h-4 w-4" />
            {t("promotions.new")}
          </Link>
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          {t("promotions.loadError")}
        </div>
      ) : !data || data.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center text-muted-foreground">
            <Tag className="h-8 w-8" />
            <div>
              <p className="font-medium text-foreground">
                {t("promotions.emptyTitle")}
              </p>
              <p className="mt-1 text-sm">{t("promotions.emptyDesc")}</p>
            </div>
            <Button asChild variant="outline">
              <Link to="/promotions/new">
                <Plus className="h-4 w-4" />
                {t("promotions.new")}
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {data.map((p) => (
            <PromotionRow key={p.id} promo={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function PromotionRow({ promo }: { promo: Promotion }) {
  const t = useT();
  const now = new Date();
  const starts = promo.startsAt ? new Date(promo.startsAt) : null;
  const ends = promo.endsAt ? new Date(promo.endsAt) : null;

  const status = getStatus(promo, now, starts, ends);

  return (
    <Link to={`/promotions/${promo.id}`} className="block">
      <Card className="transition-colors hover:bg-accent/40">
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <CardTitle className="truncate text-base">{promo.name}</CardTitle>
              <Badge variant={status.variant}>{t(`promotions.status.${status.key}`)}</Badge>
              {promo.code ? (
                <Badge variant="outline" className="font-mono">
                  {promo.code}
                </Badge>
              ) : (
                <Badge variant="secondary">{t("promotions.automatic")}</Badge>
              )}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {describePromotion(promo, t)}
            </p>
            <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
              <span>
                {t("promotions.usage")}: {promo.usageCount}
                {promo.maxUses ? ` / ${promo.maxUses}` : ""}
              </span>
              {promo.eventTitle ? (
                <span>
                  {t("promotions.scope")}: {promo.eventTitle}
                </span>
              ) : (
                <span>{t("promotions.scopeAll")}</span>
              )}
              {starts ? (
                <span>
                  {t("promotions.starts")}: {formatDT(starts)}
                </span>
              ) : null}
              {ends ? (
                <span>
                  {t("promotions.ends")}: {formatDT(ends)}
                </span>
              ) : null}
            </div>
          </div>
        </CardHeader>
      </Card>
    </Link>
  );
}

type StatusVariant = "success" | "warning" | "secondary" | "outline";

function getStatus(
  promo: Promotion,
  now: Date,
  starts: Date | null,
  ends: Date | null,
): { key: "active" | "scheduled" | "expired" | "paused"; variant: StatusVariant } {
  if (!promo.isActive) return { key: "paused", variant: "secondary" };
  if (ends && now > ends) return { key: "expired", variant: "outline" };
  if (starts && now < starts) return { key: "scheduled", variant: "warning" };
  return { key: "active", variant: "success" };
}

function describePromotion(
  p: Promotion,
  t: ReturnType<typeof useT>,
): string {
  switch (p.type) {
    case "BuyXGetY":
      return t("promotions.describeBogo", { buyX: p.buyX ?? 0, getY: p.getY ?? 0 });
    case "Percentage":
      return t("promotions.describePercentage", { value: p.discountValue ?? 0 });
    case "FixedAmount":
      return t("promotions.describeFixed", {
        value: p.discountValue ?? 0,
        currency: p.discountCurrency ?? "",
      });
  }
}

const DT_FMT = new Intl.DateTimeFormat("tr-TR", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Istanbul",
});

function formatDT(d: Date) {
  return DT_FMT.format(d);
}
